import { useState, useCallback } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { trackEvent } from '../lib/analytics';

/**
 * This custom hook encapsulates file parsing logic (CSV/XLSX).
 * It exposes the file, parsed data, row count, errorMessage, and parseFile method.
 */
export function useDataParser() {
  const [file, setFile] = useState(null);
  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Utility function for processing the final parsed rows
   */
  const processResults = (results, inputFileName) => {
    // Filter out empty rows
    const processedData = results.data.filter(
      (row) =>
        row &&
        row.some((cell) => {
          const cellString = String(cell);
          return cellString.trim() !== '';
        })
    );

    setData(processedData);
    setCount(processedData.length - 1);

    // Track parse completion using unified event naming
    if (processedData.length > 0) {
      trackEvent('file_upload_completed', {
        record_count: processedData.length - 1,
        file_name: inputFileName,
      });
    }

    // If too many rows, set error
    if (processedData.length > 100001) {
      setErrorMessage(
        'File contains more than 100,000 rows. Please break up the file and re-upload.'
      );
    } else {
      setErrorMessage('');
    }
  };

  /**
   * Parses CSV files using Papa.parse
   */
  const parseCSV = useCallback((inputFile) => {
    Papa.parse(inputFile, {
      header: false,
      skipEmptyLines: 'greedy',
      complete: (results) => processResults(results, inputFile.name),
      error: (err) => {
        trackEvent('file_upload_error', {
          error_message: err.message,
          file_name: inputFile.name,
        });
        setErrorMessage(`Error parsing CSV: ${err.message}`);
      },
    });
  }, []);

  /**
   * Parses XLSX files
   */
  const parseXLSX = useCallback((inputFile) => {
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary', raw: true });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const results = { data: XLSX.utils.sheet_to_json(ws, { header: 1 }) };
        processResults(results, inputFile.name);
      } catch (err) {
        trackEvent('file_upload_error', {
          error_message: err.message,
          file_name: inputFile.name,
        });
        setErrorMessage(`Error parsing XLSX: ${err.message}`);
      }
    };
    reader.readAsBinaryString(inputFile);
  }, []);

  /**
   * Single point of entry for parsing CSV or XLSX
   */
  const parseFile = useCallback(
    async (inputFile) => {
      const startTime = Date.now();
      setIsLoading(true);

      try {
        setErrorMessage(''); // Clear previous errors first

        // Validation BEFORE setting file state
        const fileName = inputFile.name.toLowerCase();
        const validExtensions = ['.csv', '.xlsx', '.xls'];
        const isValid = validExtensions.some((ext) => fileName.endsWith(ext));

        if (!isValid) {
          const fileExt = fileName.split('.').pop().toUpperCase();
          setErrorMessage(`Invalid file type (${fileExt}) - Only CSV/XLSX/XLS files are allowed`);
          trackEvent('file_upload_invalid_file_type', { file_type: inputFile.type });
          setFile(inputFile); // Keep reference but with error
          return;
        }

        // Set file if validation passes
        setFile(inputFile);

        trackEvent('file_upload_started', {
          file_size: inputFile.size,
          file_type: inputFile.type,
          file_name: inputFile.name,
        });

        if (fileName.endsWith('.csv')) {
          parseCSV(inputFile);
        } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
          parseXLSX(inputFile);
        } else {
          setErrorMessage(`Unsupported file type: ${fileName}. Please upload CSV or XLSX.`);
        }
      } catch (error) {
        trackEvent('file_upload_parse_error', {
          error: error.message,
          file_name: inputFile?.name,
        });
        setErrorMessage(`Processing failed: ${error.message}`);
      } finally {
        const elapsed = Date.now() - startTime;
        if (elapsed < 1000) {
          await new Promise((resolve) => setTimeout(resolve, 1000 - elapsed));
        }
        setIsLoading(false);
      }
    },
    [parseCSV, parseXLSX]
  );

  const resetParser = useCallback(() => {
    setFile(null);
    setData([]);
    setCount(0);
    setErrorMessage('');
  }, []);

  return {
    isLoading,
    file,
    data,
    count,
    errorMessage,
    parseFile,
    setErrorMessage,
    resetParser,
  };
}
